import { IVeriTableHeader, IVeriTableRow, IVeriTableRowCell, VeriTableRowStatus } from "@directives/_veri/veriTable/veriTable.component";
import { VeriTableService } from "@directives/_veri/veriTable/veriTable.service";
import { SortType } from "@enums/SortType";
import { IDriver } from "@interfaces/IDriver";
import { DriverService } from "@services/driver.service";
import { GroupService } from "@services/group.service.ajs";
import { PlotService } from "@services/plot.service";
import { TranslationService } from "@services/translation.service";
import { VehicleService } from "@services/vehicle.service";
import { VeriDate } from "@services/veriDate.service";
import * as angular from "angular";
import copy from "fast-copy/dist/fast-copy.esm.js";
import * as moment from "moment";
import { AlcolockEventGroup } from "./alcolock.enums";
import { IAlcolockResult, ICsvAlcolockRecord } from "./alcolock.interfaces";
import { AlcolockService } from "./alcolock.service";
import "./alcolockCompliance.scss";

class AlcolockComplianceComponentController implements angular.IComponentController {
    loading = false;

    alcolockResults: IAlcolockResult[] = [];
    selectedResult: IAlcolockResult;

    tableHeaders: IVeriTableHeader[] = [];
    tableRows: IVeriTableRow<IAlcolockResult>[] = [];

    selectedVehicleResults: IAlcolockResult[] = [];
    selectedVehicleTableHeaders: IVeriTableHeader[] = [];
    selectedVehicleTableRows: IVeriTableRow<IAlcolockResult>[] = [];

    selectedVehicleNumPasses: number;
    selectedVehicleNumEvents: number;
    selectedVehicleNumFails: number;

    date: Date;
    maxDate: Date = new Date();

    popupEvent: MouseEvent;
    highlightedRecord: IAlcolockResult;

    static $inject = ["$q", "veriDate", "alcolockService", "groupService", "vehicleService", "driverService", "plotService", "veriTableService", "translationService"];

    constructor(
        private readonly $q: angular.IQService,
        private readonly veriDate: VeriDate,
        private readonly alcolockService: AlcolockService,
        private readonly groupService: GroupService,
        private readonly vehicleService: VehicleService,
        private readonly driverService: DriverService,
        private readonly plotService: PlotService,
        private readonly veriTableService: VeriTableService,
        private readonly translationService: TranslationService) {
    }

    $onInit() {

        // Set initial date
        this.date = moment().startOf("isoWeek").toDate();

        this.createHeaders();
        this.createSelectedVehicleHeaders();
        this.updateData(true);
    }

    $onChanges(onChangesObj: angular.IOnChangesObject): void {

    }

    onlyMondaysPredicate(date: Date): boolean {
        const day = date.getDay();
        return day === 1;
    }

    loadHistoryForDate() {

        this.selectedVehicleNumPasses = 0;
        this.selectedVehicleNumEvents = 0;
        this.selectedVehicleNumFails = 0;
        this.selectedVehicleResults = [];

        const dateStart = this.date;
        const dateEnd = moment(this.date).add(7, "days").toDate();

        //console.log("date range is:", dateStart, dateEnd);


        this.alcolockService.getVehicleAlcolockResults(this.selectedResult.VehicleId, moment(dateStart).toISOString(), moment(dateEnd).toISOString()).then(results => {

            //console.log("results=", results);

            for (let i = 0; i < results.length; i++) {
                const result = results[i];

                switch (result.ResultType) {
                    case AlcolockEventGroup.Fail:
                        this.selectedVehicleNumFails++;
                        break;

                    case AlcolockEventGroup.Pass:
                        this.selectedVehicleNumPasses++;
                        break;

                    case AlcolockEventGroup.Circumvention:
                        this.selectedVehicleNumEvents++;
                        break;
                }

                this.selectedVehicleResults.push(result);
            }

            this.createSelectedVehicleRows();
        });
    }

    downloadCsv() {
        var list: ICsvAlcolockRecord[] = [];

        this.driverService.getDrivers().then(drivers => {

            this.selectedVehicleResults.forEach(item => {
                list.push(this.mapResultToCsvFormat(item, drivers));
            });

            this.alcolockService.downloadCsv(list, `${this.selectedResult.Vehicle.ShortReg}_${moment().toDate().toISOString()}.csv`);
        });
    }

    onRowClicked(row: IVeriTableRow<IAlcolockResult>) {
        this.selectedResult = copy(row.data);
        this.loadHistoryForDate();
    }

    onRowHighlighted(row: IVeriTableRow<IAlcolockResult>, $event: MouseEvent) {
        this.popupEvent = $event;

        if (!row) {
            return;
        }

        this.highlightedRecord = row.data;
        if (this.highlightedRecord.DriverId) {
            this.highlightedRecord.Driver = this.driverService.getCachedDriver(this.highlightedRecord.DriverId);
        }

        this.vehicleService.getVehicle(this.highlightedRecord.VehicleId, { includePeripheralMappings: true }).then(vehicle => {
            this.highlightedRecord.Vehicle = vehicle;
        });

        this.plotService
            .getPlot(this.highlightedRecord.VehicleId, this.highlightedRecord.PlotId)
            .then(plot => {
                this.highlightedRecord.Plot = plot;
            });
    }

    private updateData(showLoadingIndicator = false) {
        this.loading = showLoadingIndicator;

        const alcolockPromise = this.alcolockService.getAlcolockTop5PerVehicle(moment().add(-14, "days").toISOString(), moment().toISOString());

        this.$q.all([alcolockPromise])
            .then(([alcolockResults]) => {

                this.alcolockResults = alcolockResults;
                this.createRows();
            })
            .finally(() => {
                this.loading = false;
            });
    }

    private createHeaders() {
        this.tableHeaders = [
            {
                title: this.translationService.t("AlcolockCompliance.LastResult"),
                sortable: true,
                sortType: SortType.Number,
                className: "border-spacer"
            },
            {
                title: this.translationService.t("AlcolockCompliance.LastReading"),
                sortable: true,
                sortType: SortType.Number
            },
            {
                title: this.translationService.t("AlcolockCompliance.History"),
                sortable: true,
                sortType: SortType.Number
            },
            {
                title: this.translationService.t("Common.Vehicle"),
                sortable: true,
                templateUrl: "ac-cell1.html",
                width: 100
            },
            {
                title: this.translationService.t("Common.Details"),
                sortable: true
            },
            this.veriTableService.createDateHeader()];
    }

    private createRows() {
        this.tableRows = [];

        this.alcolockResults.filter(r => r.Show).forEach(result => {

            var row: IVeriTableRow<IAlcolockResult> = {
                data: result,
                cells: this.createTableCells(result),
                rowClassName: this.alcolockTypeToClassName(result.ResultType),
                rowWarningStatus: this.alcolockResultTypeToRowStatus(result.ResultType),
                isInGroup: (data, group) => {
                    return group.VehicleIds.indexOf(data.VehicleId) >= 0;
                }
            };

            this.tableRows.push(row);
        });
    }

    private createTableCells(result: IAlcolockResult): IVeriTableRowCell[] {

        let historyString = "";
        let historyExportString = "";
        const results = this.alcolockResults.filter(m => m.VehicleId === result.VehicleId);

        for (let i = 0; i < results.length; i++) {
            const historicalResult = results[i];
            let className = "";
            let iconName = "";

            switch (historicalResult.ResultType) {
                case AlcolockEventGroup.Pass:
                    className = "text-muted";
                    iconName = "check";
                    break;

                case AlcolockEventGroup.Fail:
                    className = "pastel-red";
                    iconName = "warning";
                    break;
                case AlcolockEventGroup.Circumvention:
                    className = "pastel-orange";
                    iconName = "warning";
                    break;
                default:
                    className = "text-muted";
                    iconName = "remove";
                    break;
            }

            historyString += `<i title="{{'AlcolockCompliance.LastFiveResults' | translate}}" class='material-icons s12 ${className}'>${iconName}</i>`;
            historyExportString += `${this.translationService.t("AlcolockCompliance."+AlcolockEventGroup[historicalResult.ResultType])}, `;
        }

        const cells: IVeriTableRowCell[] = [
            { contents: `${this.mapEventName(result.ResultType)}`, sortData: result.ResultType, className: this.alcolockTypeToClassName(result.ResultType) },
            { contents: result.FormattedReading, sortData: result.Reading },
            { contents: historyString, exportData: historyExportString },
            this.veriTableService.createVehicleRegCell(result.Vehicle),
            { contents: `<span title='${result.Details}'>${result.Details}</span>`, exportData: result.Details },
            { contents: this.veriDate.formatDate(result.DateTime), sortData: result.DateTime }
        ];

        return cells;
    }

    private mapResultToCsvFormat(result: IAlcolockResult, drivers: IDriver[]): ICsvAlcolockRecord {


        return {
            Time: this.veriDate.formatDate(result.DateTime, "time"),
            Date: this.veriDate.formatDate(result.DateTime, "date"),
            Vehicle: this.selectedResult.Vehicle.ShortReg,
            Reading: result.FormattedReading,
            Details: result.Details,
            Handset: result.PortableDeviceSerial
        };
    }

    private mapEventName(alcolockEventGroup: AlcolockEventGroup): string {
        switch (alcolockEventGroup) {

            case AlcolockEventGroup.Pass:
                return this.translationService.t("AlcolockCompliance.Pass");

            case AlcolockEventGroup.Fail:
                return this.translationService.t("AlcolockCompliance.Fail");

            case AlcolockEventGroup.Circumvention:
                return this.translationService.t("AlcolockCompliance.Circumvention");

            default:
                return this.translationService.t("AlcolockCompliance.Diagnostics");
        }
    }

    private createSelectedVehicleHeaders() {
        this.selectedVehicleTableHeaders = [
            {
                title: this.translationService.t("AlcolockCompliance.Reading"),
                sortable: true,
                sortType: SortType.Number,
                width: 100,
            },
            {
                title: this.translationService.t("Common.Details"),
                sortable: true
            },
            this.veriTableService.createDateHeader()
        ];
    }

    private createSelectedVehicleRows() {
        this.selectedVehicleTableRows = [];

        this.selectedVehicleResults.forEach(result => {
            var row: IVeriTableRow<IAlcolockResult> = {
                data: result,
                cells: this.createSelectedVehicleTableCells(result),
                rowWarningStatus: this.alcolockResultTypeToRowStatus(result.ResultType)
            };

            this.selectedVehicleTableRows.push(row);
        });
    }

    private createSelectedVehicleTableCells(result: IAlcolockResult): IVeriTableRowCell[] {
        const roundedReading = Math.round(result.Reading * 100) / 100;

        const cells: IVeriTableRowCell[] = [
            { contents: result.FormattedReading, sortData: roundedReading },
            { contents: `<span title='${result.Details}'>${result.Details}</span>`, exportData: result.Details },
            { contents: this.veriDate.formatDate(result.DateTime), sortData: result.DateTime }
        ];

        return cells;
    }

    private alcolockTypeToClassName(resultType: AlcolockEventGroup) {
        switch (resultType) {
            case AlcolockEventGroup.Pass:
                return "pass";

            case AlcolockEventGroup.Fail:
                return "error";

            case AlcolockEventGroup.Circumvention:
                return "warning";

            default:
                return "ok";
        }
    }

    private alcolockResultTypeToRowStatus(resultType: AlcolockEventGroup) {

        switch (resultType) {
            case AlcolockEventGroup.Pass:
                return VeriTableRowStatus.OkChecked;

            case AlcolockEventGroup.Fail:
                return VeriTableRowStatus.Error;

            case AlcolockEventGroup.Circumvention:
                return VeriTableRowStatus.Warning;

            case AlcolockEventGroup.SignificantEvent:
                return VeriTableRowStatus.OkDash;

            default:
                return VeriTableRowStatus.Error;
        }
    }
}

export class AlcolockComplianceComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: any;

    constructor() {
        this.bindings = {
        };

        this.controller = AlcolockComplianceComponentController;
        this.template = require("./alcolockCompliance.html");
    }
}
